// src/sms-services/agentService.ts
import axios from "axios"
import { supabase } from "../supabaseClient"

/** Axios instance pointing to our SMS API */
const api = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL ? `${process.env.REACT_APP_SERVER_URL}/api/sms` : "/api/sms",
  headers: {
    "Content-Type": "application/json",
  },
})

// Add a request interceptor to include the user ID in the headers
api.interceptors.request.use(
  async (config) => {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (session) {
      config.headers["user-id"] = session.user.id

      const { data: profile } = await supabase.from("profiles").select("role").eq("id", session.user.id).single()

      if (profile && ["admin","account_admin","super_admin"].includes(profile.role)) {
        const selectedCompanyName = localStorage.getItem("selectedCompanyName")
        if (selectedCompanyName) {
          config.headers["x-company-name"] = selectedCompanyName
          console.log("[v0] SMS Service - Setting x-company-name header:", selectedCompanyName)
        }
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

export interface SMSAgent {
  id: number
  agent_id: string
  company_name: string
  type: string
  created_at: string
  updated_at: string
  is_active: boolean
  // Fields from sms_agent_details
  name: string
  llm?: string
  language?: string
  prompt?: string
  max_chat_length?: number
  tone?: string
  tools?: string[]
  initial_message?: string
  llm_temperature?: number // Added temperature field
}

export interface CreateSMSAgentParams {
  name: string
  model?: string
  language?: string
  company_name?: string
  temperature?: number
}

export interface UpdateSMSAgentParams {
  name?: string
  llm?: string
  language?: string
  prompt?: string
  max_chat_length?: number
  tone?: string
  tools?: string[]
  initial_message?: string
  llm_temperature?: number // Added temperature field
}

export const smsAgentService = {
  list: async (companyName?: string) => {
    const url = companyName ? `/agents?company_name=${encodeURIComponent(companyName)}` : "/agents"
    const response = await api.get<SMSAgent[]>(url)
    return response.data
  },

  get: async (agentId: string) => {
    const response = await api.get<SMSAgent>(`/agents/${agentId}`)
    return response.data
  },

  create: async (params: CreateSMSAgentParams) => {
    const response = await api.post<SMSAgent>("/agents", params)
    return response.data
  },

  delete: async (agentId: string) => {
    await api.delete(`/agents/${agentId}`)
  },

  update: async (agentId: string, params: UpdateSMSAgentParams) => {
    const response = await api.patch<SMSAgent>(`/agents/${agentId}`, params)
    return response.data
  },

  getKnowledgeBases: async (agentId: string): Promise<string[]> => {
    const response = await api.get<{ knowledge_base_ids: string[] }>(`/agents/${agentId}/knowledge-bases`)
    return response.data.knowledge_base_ids
  },

  addKnowledgeBase: async (agentId: string, knowledgeBaseId: string): Promise<void> => {
    await api.post(`/agents/${agentId}/knowledge-bases`, { knowledge_base_id: knowledgeBaseId })
  },

  removeKnowledgeBase: async (agentId: string, knowledgeBaseId: string): Promise<void> => {
    await api.delete(`/agents/${agentId}/knowledge-bases/${knowledgeBaseId}`)
  },

  updateKnowledgeBases: async (agentId: string, knowledgeBaseIds: string[]): Promise<void> => {
    await api.put(`/agents/${agentId}/knowledge-bases`, { knowledge_base_ids: knowledgeBaseIds })
  },
}
